import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import "./ConfirmDialog.css";

const ConfirmContext = createContext(null);

// Renders a single, reusable confirmation modal instead of
// window.confirm()/window.prompt(). Mount <ConfirmProvider> once near the
// app root, then from any component:
//
//   const confirm = useConfirm();
//   const ok = await confirm({ title: "Remove this?", danger: true });
//   if (!ok) return;
//
// Or, to replace window.prompt():
//
//   const reason = await confirm({
//     title: "Reject visitor",
//     input: { label: "Reason", required: true },
//   });
//   if (reason === null) return; // cancelled
export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const resolverRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const inputRef = useRef(null);

  const confirm = (options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setInputValue(options.input?.defaultValue || "");
      setDialog({
        title: options.title || "Are you sure?",
        message: options.message || "",
        confirmLabel: options.confirmLabel || "Confirm",
        cancelLabel: options.cancelLabel || "Cancel",
        danger: !!options.danger,
        input: options.input || null,
      });
    });
  };

  const settle = (result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setDialog(null);
    if (resolve) resolve(result);
  };

  const handleCancel = () => settle(dialog?.input ? null : false);

  const handleConfirm = () => {
    if (dialog?.input) {
      const trimmed = inputValue.trim();
      if (dialog.input.required && !trimmed) return;
      settle(trimmed);
    } else {
      settle(true);
    }
  };

  useEffect(() => {
    if (!dialog) return undefined;
    // Focus the most useful control as soon as the dialog mounts.
    const toFocus = dialog.input ? inputRef.current : confirmBtnRef.current;
    toFocus?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      } else if (e.key === "Enter" && !dialog.input) {
        e.preventDefault();
        handleConfirm();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog, inputValue]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div className="confirm-overlay" onMouseDown={handleCancel}>
          <div
            className="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 id="confirm-dialog-title">{dialog.title}</h3>
            {dialog.message && <p className="confirm-message">{dialog.message}</p>}

            {dialog.input && (
              <div className="confirm-input-group">
                {dialog.input.label && (
                  <label htmlFor="confirm-dialog-input">{dialog.input.label}</label>
                )}
                <textarea
                  id="confirm-dialog-input"
                  ref={inputRef}
                  rows={3}
                  placeholder={dialog.input.placeholder || ""}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              </div>
            )}

            <div className="confirm-actions">
              <button type="button" className="confirm-btn confirm-btn-cancel" onClick={handleCancel}>
                {dialog.cancelLabel}
              </button>
              <button
                type="button"
                ref={confirmBtnRef}
                className={`confirm-btn confirm-btn-confirm${dialog.danger ? " danger" : ""}`}
                onClick={handleConfirm}
                disabled={dialog.input?.required && !inputValue.trim()}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm() must be called from inside <ConfirmProvider>");
  }
  return ctx;
}